import { DragDropContext, Draggable, type DropResult, Droppable } from "@hello-pangea/dnd";
import { API } from "@swivl-mono/api-helper";
import {
  filterClasses,
  normalizeCallScriptSteps,
  renderCallScript,
  renderCallScriptsForPrompt,
  resolveCallScripts,
  resolveStepLibrary,
  validateCallScriptSteps
} from "@swivl-mono/helpers";
import type { ResolvedLibraryStep } from "@swivl-mono/helpers";
import type {
  CallScriptInterface,
  CallScriptLibraryStep,
  CallScriptRequirement,
  CallScriptSequenceUse,
  CallScriptSnapshot,
  CallScriptStep,
  CallScriptVersionInterface,
  ResolvedCallScript
} from "@swivl-mono/types";
import {
  BOT_SEQUENCE_TYPE_LABELS,
  BotSequenceType,
  CALL_SCRIPTS_PROMPT_MAX_CHARS,
  CALL_SCRIPT_MAX_PER_SCOPE,
  CALL_SCRIPT_NAME_MAX_CHARS,
  CALL_SCRIPT_REQUIREMENTS,
  CALL_SCRIPT_REQUIREMENT_KEYS,
  CALL_SCRIPT_STEP_GROUPS,
  CALL_SCRIPT_WHEN_TO_USE_MAX_CHARS,
  LocationVarInheritance,
  callScriptStepAllowsRequirements,
  findCallScriptLibraryStep
} from "@swivl-mono/types";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  InheritancePill,
  Input,
  Panel,
  PanelPosition,
  Popover,
  PopoverPosition,
  Switch,
  TextArea
} from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";
import { IoChevronDown, IoChevronUp, IoClose, IoReorderThree } from "react-icons/io5";

import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import MembersModel from "../../../Models/Member/Members.Model";

/**
 * Call Scripts (ENG-3356) — the operator-named scripts in force at the page's
 * scope; the body of the Call Scripts page (Details v3, after Escalation & Ticketing). Rows come from the server; the
 * SAME resolver the bot's prompt uses (`resolveCallScripts`) decides what is
 * in force here, and the SAME renderer shows the operator exactly what the
 * bot will read. Inheritance mirrors the routing-group cards: nearest scope
 * wins per script, "Revert to inherited" deletes the override.
 */

interface CallScriptsSectionProps {
  orgId: number;
  activeGroupId: number | null;
  activeLocationId: number | null;
  onError: (message: string | null) => void;
}

interface ScriptCardModel {
  resolved: ResolvedCallScript;
  inheritance: LocationVarInheritance;
  /** The row at THIS scope, when the script is customized here. */
  rowHere: CallScriptInterface | null;
  /** Whether removing the row here would leave an inherited (or platform) version behind. */
  hasBroaderVersion: boolean;
}

function scopeMatches(row: Pick<CallScriptInterface, "groupId" | "locationId">, groupId: number | null, locationId: number | null): boolean {
  if (locationId != null) return row.locationId === locationId;
  if (groupId != null) return row.groupId === groupId && row.locationId == null;
  return row.groupId == null && row.locationId == null;
}

function inheritanceFor(resolved: ResolvedCallScript, rowHere: CallScriptInterface | null): LocationVarInheritance {
  if (rowHere) return LocationVarInheritance.NONE;
  if (resolved.source === "platform") return LocationVarInheritance.DEFAULT;
  if (resolved.source === "group") return LocationVarInheritance.GROUP;
  return LocationVarInheritance.ORG;
}

function stepLabel(step: CallScriptStep, library: ReadonlyArray<CallScriptLibraryStep>): string {
  return findCallScriptLibraryStep(step.slug, library)?.label ?? step.slug;
}

function memberName(memberId: number | null): string {
  if (memberId == null) return "Swivl";
  const member = MembersModel.state.members?.find((m) => m.id === memberId);
  return member?.agentName || member?.account?.email || `Member #${memberId}`;
}

export function CallScriptsSection(props: CallScriptsSectionProps): React.ReactElement {
  const { orgId, activeGroupId, activeLocationId, onError } = props;
  const page = usePropertiesGetLocationIds();
  const [rows, setRows] = useState<CallScriptInterface[]>([]);
  /** Where scripts are bound as a sequence day's starting script (ENG-3403) — read-only here. */
  const [sequenceUses, setSequenceUses] = useState<Array<CallScriptSequenceUse & { slug: string }>>([]);
  /** The org's step library — platform catalog with the org's rows applied (ENG-3402). */
  const [library, setLibrary] = useState<ResolvedLibraryStep[]>(() => resolveStepLibrary([]));
  const [loading, setLoading] = useState(true);
  /** Slug being edited, "" for a brand-new script, null when the panel is closed. */
  const [editingSlug, setEditingSlug] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    try {
      const [nextRows, uses, stepRows] = await Promise.all([
        API.CallScript.listRows(orgId),
        API.CallScript.listSequenceUses(orgId).catch(() => []),
        API.CallScriptStep.listRows(orgId).catch(() => [])
      ]);
      setRows(nextRows.map((row) => ({ ...row, steps: normalizeCallScriptSteps(row.steps) })));
      setSequenceUses(uses);
      setLibrary(resolveStepLibrary(stepRows));
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeGroupId, activeLocationId]);

  const scope = useMemo(() => ({ groupId: activeGroupId, locationId: activeLocationId }), [activeGroupId, activeLocationId]);

  const cards: ScriptCardModel[] = useMemo(() => {
    const resolved = resolveCallScripts(rows, scope);
    return resolved.map((r) => {
      const rowHere = rows.find((row) => row.slug === r.slug && scopeMatches(row, activeGroupId, activeLocationId)) ?? null;
      const withoutHere = rowHere ? rows.filter((row) => row.id !== rowHere.id) : rows;
      const hasBroaderVersion = resolveCallScripts(withoutHere, scope).some((s) => s.slug === r.slug);
      return { resolved: r, inheritance: inheritanceFor(r, rowHere), rowHere, hasBroaderVersion };
    });
  }, [rows, scope, activeGroupId, activeLocationId]);

  /** Scripts removed at this scope (a row here with no steps) — shown muted so the removal can be undone. */
  const removedHere = useMemo(
    () => rows.filter((row) => scopeMatches(row, activeGroupId, activeLocationId) && row.steps.length === 0),
    [rows, activeGroupId, activeLocationId]
  );

  const scopeBody = { groupId: activeLocationId != null ? null : activeGroupId, locationId: activeLocationId };

  const handleSave = async (slug: string | undefined, snapshot: CallScriptSnapshot, expectedRevision?: number): Promise<void> => {
    onError(null);
    await API.CallScript.upsert(orgId, { slug, ...snapshot, ...scopeBody, expectedRevision });
    await reload();
  };

  const handleRevert = async (row: CallScriptInterface): Promise<void> => {
    onError(null);
    await API.CallScript.remove(orgId, row.id);
    await reload();
  };

  const editingCard = editingSlug ? (cards.find((c) => c.resolved.slug === editingSlug) ?? null) : null;
  const otherScripts = cards.filter((c) => c.resolved.slug !== editingSlug).map((c) => c.resolved);
  const atCap = cards.length >= CALL_SCRIPT_MAX_PER_SCOPE;

  return (
    <section className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="text-base font-semibold text-grey-900">Call Scripts</h2>
          </div>
          <p className="pl-3 text-base text-grey-600">
            The steps the bot follows, in your order, when a conversation matches a script. Every script here goes into the bot&apos;s instructions;
            it picks the one whose &ldquo;use this when&rdquo; fits.
          </p>
        </div>
        <Button color={ButtonColor.BLACK} disabled={loading || atCap} onClick={() => setEditingSlug("")}>
          Add script
        </Button>
      </div>
      {atCap && (
        <p className="pl-3 text-sm text-grey-500">Up to {CALL_SCRIPT_MAX_PER_SCOPE} scripts can be in force here. Remove one to add another.</p>
      )}

      {loading ? (
        <p className="pl-3 text-sm text-grey-500">Loading…</p>
      ) : (
        <div className="space-y-3">
          {cards.map((card) => (
            <ScriptCard
              key={card.resolved.slug}
              card={card}
              library={library}
              sequenceUses={sequenceUses.filter((u) => u.slug === card.resolved.slug)}
              onEdit={() => setEditingSlug(card.resolved.slug)}
            />
          ))}
          {removedHere.map((row) => (
            <div
              key={`removed-${row.id}`}
              className="flex items-center justify-between gap-4 rounded-lg border border-dashed border-grey-200 bg-grey-50 p-4"
            >
              <p className="text-sm text-grey-600">
                <span className="font-semibold text-grey-900">{row.name}</span> is removed at this scope.
              </p>
              <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} onClickAsync={() => handleRevert(row)}>
                Restore inherited
              </Button>
            </div>
          ))}
        </div>
      )}

      <Panel
        isOpen={editingSlug !== null}
        onToggle={() => setEditingSlug(null)}
        position={PanelPosition.RIGHT}
        width="560px"
        title={editingCard ? `Edit ${editingCard.resolved.name}` : "New call script"}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        shouldIgnoreOutsideClick={(e) =>
          e.target instanceof Element && !!e.target.closest("[data-radix-popper-content-wrapper],[data-floating-ui-portal]")
        }
      >
        {editingSlug !== null && (
          <CallScriptEditor
            key={editingSlug}
            orgId={orgId}
            card={editingCard}
            otherScripts={otherScripts}
            library={library}
            existingSlugs={cards.map((c) => c.resolved.slug)}
            sequenceUses={editingCard ? sequenceUses.filter((u) => u.slug === editingCard.resolved.slug) : []}
            sequencesPagePath={`${page.preamble}/page/sequences`}
            onSave={async (snapshot) => {
              await handleSave(editingCard?.resolved.slug, snapshot, editingCard?.rowHere?.revision);
              setEditingSlug(null);
            }}
            onRemoveHere={
              editingCard && editingCard.inheritance !== LocationVarInheritance.NONE
                ? async () => {
                    await handleSave(editingCard.resolved.slug, {
                      name: editingCard.resolved.name,
                      whenToUse: editingCard.resolved.whenToUse,
                      steps: [],
                      forSequencesOnly: !!editingCard.resolved.forSequencesOnly
                    });
                    setEditingSlug(null);
                  }
                : undefined
            }
            onRevert={
              editingCard?.rowHere
                ? async () => {
                    const row = editingCard.rowHere as CallScriptInterface;
                    const message = editingCard.hasBroaderVersion
                      ? `Revert '${row.name}' to the inherited version? Your changes at this scope are removed (they stay in the history).`
                      : `Delete '${row.name}'? It has no inherited version to fall back to.`;
                    if (!confirm(message)) return;
                    await handleRevert(row);
                    setEditingSlug(null);
                  }
                : undefined
            }
            onCancel={() => setEditingSlug(null)}
            onError={onError}
            onRestored={reload}
          />
        )}
      </Panel>
    </section>
  );
}

/** "Past due · Day 3" — how a sequence-day binding reads on the script (ENG-3403). */
function sequenceUseLabel(use: CallScriptSequenceUse): string {
  const type = BOT_SEQUENCE_TYPE_LABELS[use.sequenceType as BotSequenceType] ?? use.sequenceType;
  return `${type} · Day ${use.offsetDays}`;
}

interface ScriptCardProps {
  card: ScriptCardModel;
  library: ReadonlyArray<CallScriptLibraryStep>;
  sequenceUses: CallScriptSequenceUse[];
  onEdit: () => void;
}

function ScriptCard(props: ScriptCardProps): React.ReactElement {
  const { resolved, inheritance } = props.card;
  const { library } = props;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-grey-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-base font-semibold text-grey-900">{resolved.name}</p>
            <InheritancePill
              inheritance={inheritance}
              isEditing={false}
              canRevert={false}
              alwaysShow
              customText={{ custom: "Customized" }}
              onClick={() => {}}
            />
            {resolved.forSequencesOnly && (
              <span className="rounded-full bg-grey-100 px-2 py-0.5 text-xs font-medium text-grey-700">For sequences only</span>
            )}
          </div>
          {resolved.whenToUse && (
            <p className="text-sm text-grey-600">
              <span className="font-medium text-grey-700">Use this when:</span> {resolved.whenToUse}
            </p>
          )}
          {props.sequenceUses.length > 0 && (
            <p className="text-sm text-grey-600">
              <span className="font-medium text-grey-700">Used by:</span> {props.sequenceUses.map(sequenceUseLabel).join(", ")}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={props.onEdit}
          className="flex shrink-0 cursor-pointer items-center gap-2 text-sm font-semibold text-grey-900 hover:text-orange-500"
        >
          Edit
        </button>
      </div>
      <ol className="space-y-1 pl-1 text-sm text-grey-700">
        {resolved.steps.map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="w-5 shrink-0 text-right text-grey-400">{i + 1}.</span>
            <span>
              {stepLabel(step, library)}
              {step.requirements.length > 0 && (
                <span className="text-grey-400">
                  {" "}
                  · needs {step.requirements.map((r) => CALL_SCRIPT_REQUIREMENTS[r].label.toLowerCase()).join(", ")}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

interface CallScriptEditorProps {
  orgId: number;
  /** Null for a brand-new script. */
  card: ScriptCardModel | null;
  otherScripts: ResolvedCallScript[];
  /** The org's resolved step library (ENG-3402) — the picker, labels, sentences and validation all read from it. */
  library: ResolvedLibraryStep[];
  existingSlugs: string[];
  /** Sequence days bound to this script (read-only "Used by", ENG-3403). */
  sequenceUses: CallScriptSequenceUse[];
  /** Where the bindings are edited. */
  sequencesPagePath: string;
  onSave: (snapshot: CallScriptSnapshot) => Promise<void>;
  onCancel: () => void;
  /** Present when the script is inherited here — writes an empty override so it no longer applies at this scope. */
  onRemoveHere?: () => Promise<void>;
  /** Present when a row exists at this scope — deletes it. */
  onRevert?: () => Promise<void>;
  onError: (message: string | null) => void;
  onRestored: () => Promise<void>;
}

type EditorTab = "edit" | "history";

function CallScriptEditor(props: CallScriptEditorProps): React.ReactElement {
  const { card, otherScripts, library } = props;
  const [name, setName] = useState(card?.resolved.name ?? "");
  const [whenToUse, setWhenToUse] = useState(card?.resolved.whenToUse ?? "");
  const [forSequencesOnly, setForSequencesOnly] = useState(!!card?.resolved.forSequencesOnly);
  const [steps, setSteps] = useState<CallScriptStep[]>(() =>
    card ? card.resolved.steps.map((s) => ({ ...s, requirements: [...s.requirements] })) : []
  );
  const [tab, setTab] = useState<EditorTab>("edit");
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Save/restore problems show HERE, inside the editor, next to what the operator is editing. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const [versions, setVersions] = useState<CallScriptVersionInterface[] | null>(null);
  /** Step indexes whose "must have" picker is open (steps with requirements always show it). */
  const [pickerOpen, setPickerOpen] = useState<Set<number>>(() => new Set());

  const rowHere = card?.rowHere ?? null;

  useEffect(() => {
    if (tab !== "history" || !rowHere || versions) return;
    void API.CallScript.listVersions(props.orgId, rowHere.id)
      .then(setVersions)
      .catch((e) => props.onError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, rowHere?.id]);

  const draft: CallScriptSnapshot = { name, whenToUse, steps, forSequencesOnly };
  const structuralErrors = validateCallScriptSteps(steps, library);
  const nameError = !name.trim()
    ? "Give the script a name."
    : name.trim().length > CALL_SCRIPT_NAME_MAX_CHARS
      ? `Keep it under ${CALL_SCRIPT_NAME_MAX_CHARS} characters.`
      : undefined;
  const whenError =
    whenToUse.length > CALL_SCRIPT_WHEN_TO_USE_MAX_CHARS ? `Keep it under ${CALL_SCRIPT_WHEN_TO_USE_MAX_CHARS} characters.` : undefined;

  // What the bot will read at this scope with this edit applied — same renderer, same budget.
  const budget = useMemo(() => {
    const all = [...otherScripts, { ...draft, slug: card?.resolved.slug ?? "new" }];
    return renderCallScriptsForPrompt(all, undefined, undefined, library).text.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherScripts, name, whenToUse, steps, library]);
  const overBudget = budget > CALL_SCRIPTS_PROMPT_MAX_CHARS;
  const canSave = !saving && !nameError && !whenError && structuralErrors.length === 0 && steps.length > 0 && !overBudget;

  const usedSlugs = new Set(steps.map((s) => s.slug));
  const addable = library.filter((s) => !usedSlugs.has(s.slug));
  // The picker lists the resolved library grouped the way the Steps tab is (John's nine groups).
  const addableGroups = CALL_SCRIPT_STEP_GROUPS.map((g) => ({ ...g, steps: addable.filter((s) => s.group === g.slug) })).filter(
    (g) => g.steps.length > 0
  );

  const handleReorder = (result: DropResult): void => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const next = [...steps];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setSteps(next);
  };

  const moveStep = (index: number, delta: -1 | 1): void => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    setSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  const toggleRequirement = (index: number, requirement: CallScriptRequirement): void => {
    setSteps((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const has = s.requirements.includes(requirement);
        return { ...s, requirements: has ? s.requirements.filter((r) => r !== requirement) : [...s.requirements, requirement] };
      })
    );
  };

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await props.onSave({ name: name.trim(), whenToUse: whenToUse.trim(), steps, forSequencesOnly });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (version: CallScriptVersionInterface): Promise<void> => {
    if (!rowHere) return;
    if (!confirm(`Restore revision ${version.revision}? It is saved as a new revision; nothing is lost.`)) return;
    setSaveError(null);
    try {
      await API.CallScript.restore(props.orgId, rowHere.id, version.revision);
      await props.onRestored();
      props.onCancel();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex h-[50px] shrink-0 items-center gap-2 border-b border-grey-200 px-4">
        <p className="min-w-0 flex-1 truncate text-base font-semibold text-grey-900">{card ? `Edit ${card.resolved.name}` : "New call script"}</p>
        {card && (
          <InheritancePill
            inheritance={card.inheritance}
            isEditing={false}
            canRevert={false}
            alwaysShow
            customText={{ custom: "Customized" }}
            onClick={() => {}}
          />
        )}
        {rowHere && (
          <div className="ml-2 flex overflow-hidden rounded border border-grey-200 text-xs">
            {(["edit", "history"] as EditorTab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={filterClasses("px-2 py-1 capitalize", tab === t ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50")}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "history" && rowHere ? (
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {!versions ? (
            <p className="text-sm text-grey-500">Loading history…</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-grey-500">No saved versions yet.</p>
          ) : (
            <ul className="space-y-2">
              {versions.map((v) => (
                <li key={v.id} className="flex items-start justify-between gap-3 rounded border border-grey-200 p-3">
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-grey-900">
                      Revision {v.revision} <span className="font-normal text-grey-500">· {v.note}</span>
                    </p>
                    <p className="text-grey-600">
                      {memberName(v.memberId)} · {v.createdAt ? new Date(v.createdAt).toLocaleString() : ""} · {v.snapshot.steps.length} step
                      {v.snapshot.steps.length === 1 ? "" : "s"}
                    </p>
                    <p className="mt-1 truncate text-grey-500">
                      {normalizeCallScriptSteps(v.snapshot.steps)
                        .map((s) => stepLabel(s, library))
                        .join(" → ") || "(no steps — removed here)"}
                    </p>
                  </div>
                  {v.revision !== rowHere.revision && (
                    <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} onClickAsync={() => handleRestore(v)}>
                      Restore
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <Input
            label="Script name"
            value={name}
            maxLength={CALL_SCRIPT_NAME_MAX_CHARS}
            error={name && nameError ? nameError : undefined}
            onChange={(e) => setName(e.target.value)}
          />
          <TextArea
            label="Use this script when…"
            value={whenToUse}
            rows={2}
            maxLength={CALL_SCRIPT_WHEN_TO_USE_MAX_CHARS}
            placeholder="e.g. A tenant wants to end their lease or asks how to move out."
            onChange={(e) => setWhenToUse(e.target.value)}
          />
          <p className="-mt-2 text-xs text-grey-500">
            One plain sentence about the customer&apos;s situation. The bot matches conversations against it. Checked when you save.
            {whenError && <span className="text-red-600"> {whenError}</span>}
          </p>

          <div className="rounded border border-grey-200 p-3">
            <div className="flex items-center gap-3">
              <Switch on={forSequencesOnly} activeColor="orange" onSet={(on) => setForSequencesOnly(on)} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-grey-900">For sequences only</p>
                <p className="text-xs text-grey-500">
                  The bot only sees this script in a conversation that started from a sequence day set to begin with it. Never in an ordinary
                  conversation.
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs text-grey-600">
              <span className="font-medium text-grey-700">Used by:</span>{" "}
              {props.sequenceUses.length > 0 ? props.sequenceUses.map(sequenceUseLabel).join(", ") : "no sequence day yet"} ·{" "}
              <a className="font-medium text-orange-600 hover:underline" href={props.sequencesPagePath}>
                set on the Sequences page
              </a>
            </p>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium text-grey-900">
                Steps, in order {steps.length > 1 && <span className="font-normal text-grey-400">· drag to reorder</span>}
              </p>
              <Popover
                isOpen={addOpen}
                onOpenChange={setAddOpen}
                position={PopoverPosition.RIGHT}
                styles={{ width: "max-content", minWidth: "260px" }}
                trigger={
                  <Button variant={ButtonVariant.DASHED} size={ButtonSize.SMALL} disabled={addable.length === 0}>
                    Add step
                  </Button>
                }
              >
                <div className="max-h-80 overflow-y-auto py-1">
                  {addableGroups.map((group) => (
                    <div key={group.slug}>
                      <p className="px-3 pt-2 pb-1 text-xs font-semibold tracking-wide text-grey-500 uppercase">{group.label}</p>
                      {group.steps.map((s) => (
                        <button
                          key={s.slug}
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-grey-50"
                          onClick={() => {
                            setSteps((prev) => [...prev, { slug: s.slug, requirements: [...s.defaultRequirements] }]);
                            setAddOpen(false);
                          }}
                        >
                          <span className="font-medium text-grey-900">{s.label}</span>
                          {s.source === "org" && <span className="ml-1 text-xs text-grey-400">· customized</span>}
                          <span className="block text-xs text-grey-500">{s.sentence}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                  {addable.length === 0 && <p className="px-3 py-2 text-sm text-grey-500">Every step in the library is already in this script.</p>}
                </div>
              </Popover>
            </div>

            {steps.length === 0 ? (
              <p className="rounded border border-dashed border-grey-200 bg-grey-50 px-3 py-4 text-sm text-grey-500">
                No steps yet — add the first one above.
              </p>
            ) : (
              <DragDropContext onDragEnd={handleReorder}>
                <Droppable droppableId="call-script-steps">
                  {(dropProvided) => (
                    <ol ref={dropProvided.innerRef} {...dropProvided.droppableProps} className="space-y-2">
                      {steps.map((step, index) => (
                        <Draggable key={step.slug} draggableId={`step-${step.slug}`} index={index}>
                          {(dragProvided) => (
                            <li
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className="rounded-md border border-grey-200 bg-grey-50 p-3"
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex shrink-0 flex-col items-center">
                                  <button
                                    type="button"
                                    {...dragProvided.dragHandleProps}
                                    aria-label="Drag to reorder step"
                                    className="cursor-grab text-grey-400 hover:text-grey-600 active:cursor-grabbing"
                                  >
                                    <IoReorderThree size={20} />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={`Move step ${index + 1} up`}
                                    disabled={index === 0}
                                    onClick={() => moveStep(index, -1)}
                                    className="text-grey-400 hover:text-grey-700 disabled:opacity-30"
                                  >
                                    <IoChevronUp size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={`Move step ${index + 1} down`}
                                    disabled={index === steps.length - 1}
                                    onClick={() => moveStep(index, 1)}
                                    className="text-grey-400 hover:text-grey-700 disabled:opacity-30"
                                  >
                                    <IoChevronDown size={14} />
                                  </button>
                                </div>
                                <span className="mt-0.5 w-5 shrink-0 text-sm text-grey-400">{index + 1}.</span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-grey-900">
                                    {stepLabel(step, library)}
                                    {findCallScriptLibraryStep(step.slug, library) === undefined && (
                                      <span className="ml-1 text-xs text-red-600">· no longer in the step library</span>
                                    )}
                                  </p>
                                  <p className="text-sm text-grey-600">{findCallScriptLibraryStep(step.slug, library)?.sentence}</p>
                                  {callScriptStepAllowsRequirements(step, library) && !(step.requirements.length > 0 || pickerOpen.has(index)) && (
                                    <button
                                      type="button"
                                      className="mt-2 text-xs font-medium text-grey-600 hover:text-orange-600"
                                      onClick={() => setPickerOpen((prev) => new Set(prev).add(index))}
                                    >
                                      + Add a must-have
                                    </button>
                                  )}
                                  {callScriptStepAllowsRequirements(step, library) && (step.requirements.length > 0 || pickerOpen.has(index)) && (
                                    <div className="mt-2 flex flex-wrap items-center gap-1">
                                      <span className="mr-1 text-xs text-grey-500">Must have:</span>
                                      {CALL_SCRIPT_REQUIREMENT_KEYS.map((r) => {
                                        const on = step.requirements.includes(r);
                                        return (
                                          <button
                                            key={r}
                                            type="button"
                                            onClick={() => toggleRequirement(index, r)}
                                            className={filterClasses(
                                              "rounded-full border px-2 py-0.5 text-xs",
                                              on
                                                ? "border-grey-900 bg-grey-900 text-white"
                                                : "border-grey-200 bg-white text-grey-600 hover:border-grey-400"
                                            )}
                                          >
                                            {CALL_SCRIPT_REQUIREMENTS[r].label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  aria-label="Remove step"
                                  onClick={() => setSteps((prev) => prev.filter((_, i) => i !== index))}
                                  className="shrink-0 text-grey-400 hover:text-red-600"
                                >
                                  <IoClose size={18} />
                                </button>
                              </div>
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </ol>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            {structuralErrors.length > 0 && <p className="mt-2 text-sm text-red-600">{structuralErrors.join(" ")}</p>}
          </div>

          <div className="rounded border border-grey-200 bg-white p-3">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs font-semibold tracking-wide text-grey-500 uppercase">What the bot reads</p>
              <p className={filterClasses("text-xs", overBudget ? "font-semibold text-red-600" : "text-grey-500")}>
                {budget.toLocaleString()} / {CALL_SCRIPTS_PROMPT_MAX_CHARS.toLocaleString()} characters across all scripts here
              </p>
            </div>
            <pre className="font-sans text-sm whitespace-pre-wrap text-grey-700">{renderCallScript(draft, library) || "(nothing yet)"}</pre>
          </div>
        </div>
      )}

      {saveError && (
        <div className="shrink-0 border-t border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {saveError}
        </div>
      )}
      <div className="flex h-[50px] shrink-0 items-center justify-between gap-2 border-t border-grey-200 bg-white px-4">
        <div className="flex items-center gap-1">
          {props.onRevert && (
            <Button variant={ButtonVariant.GHOST} className="-ml-3" onClickAsync={props.onRevert}>
              {card?.hasBroaderVersion ? "Revert to inherited" : "Delete script"}
            </Button>
          )}
          {props.onRemoveHere && (
            <Button variant={ButtonVariant.GHOST} className="-ml-3" onClickAsync={props.onRemoveHere}>
              Remove here
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant={ButtonVariant.GHOST} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button color={ButtonColor.BLACK} disabled={!canSave} onClickAsync={handleSave}>
            {saving ? "Checking & saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
